import {
  ComparisonCondition,
  MigrationOperationCondition,
  MigrationStepCondition,
  Predicate,
} from '@/model/types/migrations/condition';
import {MigrationOperation} from '@/model/types/migrations/operation';
import {compareValues} from '../../utils/predicate';

type Condition = MigrationStepCondition | MigrationOperationCondition;

type PredicateOperator = Condition['operator'];

type PredicateFactory<T extends PredicateOperator> = (
  condition: Omit<Extract<MigrationOperationCondition, {operator: T}>, 'operator'>,
) => Predicate;

type PredicateMap = {
  [T in PredicateOperator]: PredicateFactory<T>;
};

const createComparisonPredicateFactory =
  <T extends ComparisonCondition['operator']>(operator: T): PredicateFactory<T> =>
  condition => {
    return data => compareValues(data, condition.value, operator);
  };

const AndPredicate: PredicateFactory<'and'> = condition => {
  return data => condition.conditions.every(c => getOperationPredicate(c)(data));
};

const OrPredicate: PredicateFactory<'or'> = condition => {
  return data => condition.conditions.some(c => getOperationPredicate(c)(data));
};

const NotPredicate: PredicateFactory<'not'> = condition => {
  return data => !getOperationPredicate({operator: 'and', conditions: condition.conditions})(data);
};

const InPredicate: PredicateFactory<'in'> = condition => {
  return data => condition.value.some(v => getOperationPredicate({operator: '=', value: v})(data));
};

const NotInPredicate: PredicateFactory<'not in'> = condition => {
  return data => !getOperationPredicate({operator: 'in', value: condition.value})(data);
};

const predicateMap: PredicateMap = {
  '=': createComparisonPredicateFactory('='),
  '!=': createComparisonPredicateFactory('!='),
  '>': createComparisonPredicateFactory('>'),
  '>=': createComparisonPredicateFactory('>='),
  '<': createComparisonPredicateFactory('<'),
  '<=': createComparisonPredicateFactory('<='),
  'and': AndPredicate,
  'or': OrPredicate,
  'not': NotPredicate,
  'in': InPredicate,
  'not in': NotInPredicate,
};

const getOperationPredicate = <K extends PredicateOperator>(
  condition: Extract<MigrationOperationCondition, {operator: K}>,
): Predicate => {
  if (!predicateMap[condition.operator]) {
    throw new Error(`Unknown condition operator: ${condition.operator}`);
  }
  return predicateMap[condition.operator](condition);
};

export const createOperationPredicate = ({conditions}: MigrationOperation): Predicate => {
  return data => {
    if (!conditions || !conditions.length) return true;
    return conditions.every(condition => getOperationPredicate(condition)(data));
  };
};
